import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { MotifButton } from '../button/button';
import { MotifTextArea } from '../forms/text-area/text-area';
import { ReactComponent as NavigationIcClose24px } from '../../assets/icons/navigation/ic_close_24px.svg';
import { ReactComponent as ContentIcSave24px } from '../../assets/icons/content/ic_save_24px.svg';
import { ReactComponent as ContentIcReply24px } from '../../assets/icons/content/ic_reply_24px.svg';

const defaultIcons = {
  save: <ContentIcSave24px />,
  cancel: <NavigationIcClose24px />,
  reply: <ContentIcReply24px />
};

/**
 * @typedef Props
 * @prop {React.ReactNode} children
 * @prop {string} [className]
 * @prop {boolean} [edit]
 * @prop {(x: string) => void} [onSaveEdit]
 * @prop {(x: string) => void} [onCancelEdit]
 * @prop {() => void} [onReplyComment]
 * @prop {number} [previewRowCount]
 * @prop {number} [charLimit]
 * @prop {'after-children' | 'before-children' | 'null'} [replyPosition]
 * @prop {{}} [textAreaProps]
 * @prop {React.ReactNode} [actions]
 * @prop {string} author
 * @prop {React.ReactNode} [avatar]
 * @prop {string} content
 * @prop {string | React.ReactNode} [dateTime]
 * @prop {{cancel: React.ReactNode, reply: React.ReactNode, save: React.ReactNode}} [icons]
 */
/**
 * @param {Props} props
 */
const MotifComment = (props) => {
  const {
    edit = false,
    onSaveEdit = null,
    onCancelEdit = null,
    onReplyComment = null,
    previewRowCount = 3,
    charLimit = 380,
    replyPosition = 'after-children',
    textAreaProps = {},
    actions = null,
    author,
    avatar = null,
    content = '',
    dateTime = null,
    children,
    className = '',
    icons: userIcons = {}
  } = props;
  const [showMore, setShowMore] = useState(false);
  const [editorValue, setEditorValue] = useState(content);
  const [showButton, setShowButton] = useState(false);

  const editorValueRef = useRef(content);
  const contentRef = useRef();

  const icons = {
    ...defaultIcons,
    ...userIcons
  };

  useEffect(() => {
    if (contentRef.current) {
      contentRef.current.style.setProperty('--comment-preview-row-count', `${previewRowCount}`);
    }
  }, [previewRowCount]);

  useEffect(() => {
    setShowButton(
      contentRef.current && contentRef.current.scrollHeight > contentRef.current.clientHeight
    );
  }, [edit, previewRowCount]);

  const contentClasses = classNames({
    'motif-comment-text': true,
    'motif-comment-text-truncated': edit ? '' : !showMore
  });

  const contentComment = (
    <span ref={contentRef} className={contentClasses}>
      {editorValue}
    </span>
  );

  const contentOrTextArea = edit ? (
    <MotifTextArea
      placeholder="Write your reply"
      value={editorValue}
      onChange={event => setEditorValue(event.target.value)}
      maxLength={charLimit}
      className="motif-comment-edit-text-area"
      {...textAreaProps}
    />
  ) : (
    contentComment
  );

  const editActions = edit ? (
    <React.Fragment>
      <MotifButton
        variant="text-alt"
        onClick={() => {
          if (onSaveEdit) {
            onSaveEdit(editorValue);
          }
          editorValueRef.current = editorValue;
        }}
        disabled={editorValue.length === 0}
      >
        <span className="action-icons">{icons.save}</span>
        Save
      </MotifButton>
      <MotifButton
        variant="text-alt"
        onClick={() => {
          if (onCancelEdit) {
            onCancelEdit(editorValueRef.current);
          }

          setEditorValue(editorValueRef.current);
        }}
        className="cancel-edit-button"
      >
        <span className="action-icons">{icons.cancel}</span>
        Cancel
      </MotifButton>
    </React.Fragment>
  ) : null;

  const replyComment = edit ? null : (
    <MotifButton
      variant="text-alt"
      onClick={() => {
        if (onReplyComment) {
          onReplyComment();
        }
      }}
    >
      <span className="action-icons">{icons.reply}</span>
      Reply
    </MotifButton>
  );

  const renderDetails = () =>
    showButton && (
      <MotifButton
        className="motif-comment-truncation-toggle"
        variant="text-alt"
        onClick={() => setShowMore(!showMore)}
      >
        {showMore ? 'See less' : 'See more'}
      </MotifButton>
    );

  const classes = classNames({
    'motif-comment': true,
    [className]: true
  });

  const replyActionAfterClasses = classNames({
    'motif-comment-reply-after': replyPosition === 'after-children'
  });

  const replyAfterChildren = replyPosition === 'after-children' && (
    <div className={replyActionAfterClasses}>{replyComment}</div>
  );

  const messageClasses = classNames({
    'motif-comment-message': true,
    'motif-comment-padding': !showButton && !edit
  });

  const viewActionClasses = classNames({
    'motif-comment-view-action': true,
    'reply-positioned-before': replyPosition === 'before-children'
  });

  return (
    <div className={classes}>
      <div className="motif-comment-body">
        <div className="motif-comment-avatar">{avatar}</div>
        <div className="motif-comment-content">
          <div className="motif-comment-details">
            <div className="motif-comment-author">{author}</div>
            <div className="motif-comment-time">{dateTime}</div>
            <div className="motif-comment-actions">{actions}</div>
          </div>
          <div className={messageClasses}>{contentOrTextArea}</div>
          <div className="motif-comment-inline-actions">
            {edit ? (
              <div className="motif-comment-edit-action">{editActions}</div>
            ) : (
              <div className={viewActionClasses}>
                {replyPosition === 'before-children' && (
                  <div className="motif-comment-reply-before">{replyComment}</div>
                )}
                {renderDetails()}
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="motif-comment-nested">{children}</div>
      {replyAfterChildren}
    </div>
  );
};

MotifComment.propTypes = {
  edit: PropTypes.bool,
  onSaveEdit: PropTypes.func,
  onCancelEdit: PropTypes.func,
  onReplyComment: PropTypes.func,
  previewRowCount: PropTypes.number,
  charLimit: PropTypes.number,
  replyPosition: PropTypes.oneOf(['after-children', 'before-children', 'null']),
  textAreaProps: PropTypes.objectOf(PropTypes.object),
  actions: PropTypes.node,
  author: PropTypes.string,
  avatar: PropTypes.node,
  content: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  dateTime: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  children: PropTypes.node,
  className: PropTypes.string,
  icons: PropTypes.shape({
    cancel: PropTypes.node,
    reply: PropTypes.node,
    save: PropTypes.node
  })
};

export { MotifComment };
